/**
 * API Client Utility
 *
 * TEMPORARY NOTE: This utility includes support for self-signed certificates
 * due to Let's Encrypt rate limiting issues. This is a temporary workaround
 * until the rate limiting period expires and proper certificates are obtained.
 *
 * TODO: Remove self-signed certificate support once Let's Encrypt rate limiting
 * is resolved and proper certificates are in place.
 *
 * All HTTP call sites should pass path-only URLs (e.g. `/api/foo`). Absolute API
 * roots are injected only via {@link apiUrl} / {@link apiClient} / {@link apiFetch}.
 */

interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: any
  credentials?: RequestCredentials
  skipOrganizationHeader?: boolean
  /** When true, do not console.error on failed requests (caller handles expected failures). */
  quiet?: boolean
}

import { getApiUrl } from '../config'
import { getActiveOrganizationId } from './activeOrganization'
import { isPublicRecordingPath } from './publicRoutes'
import { clearSessionToken, getSessionToken } from '../auth/session'

const ORG_HEADER_SKIP_PREFIXES = ['/api/auth/', '/api/organization/invite/', '/api/organizations']

function shouldAttachOrganizationHeader(endpoint: string, hasAuthToken: boolean, skipOrganizationHeader?: boolean): boolean {
  if (!hasAuthToken || skipOrganizationHeader) return false
  if (endpoint.startsWith('/api/auth/cli/')) return true
  return !ORG_HEADER_SKIP_PREFIXES.some((prefix) => endpoint.startsWith(prefix))
}

/**
 * Single injection point: turn a path-only API URL into an absolute URL for the
 * active environment / data center. Paths may be `/api/...` or already absolute
 * (returned unchanged).
 */
export function apiUrl(path: string): string {
  const trimmed = path.trim()
  if (!trimmed) return getApiUrl()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${getApiUrl()}${normalized}`
}

export type ApiFetchOptions = RequestInit & {
  /** When true, do not attach Authorization / org headers. */
  skipAuth?: boolean
  skipOrganizationHeader?: boolean
}

/**
 * Raw fetch for SSE, FormData, blobs, and non-JSON responses.
 * Pass path-only URLs — the environment root is injected via {@link apiUrl}.
 */
export async function apiFetch(path: string, init: ApiFetchOptions = {}): Promise<Response> {
  const { skipAuth, skipOrganizationHeader, headers: initHeaders, credentials, ...rest } = init
  const headers = new Headers(initHeaders)

  if (!skipAuth) {
    const token = getSessionToken()
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    const activeOrgId = getActiveOrganizationId()
    if (
      activeOrgId &&
      shouldAttachOrganizationHeader(path, Boolean(token), skipOrganizationHeader) &&
      !headers.has('X-Organization-ID')
    ) {
      headers.set('X-Organization-ID', String(activeOrgId))
    }
  }

  const response = await fetch(apiUrl(path), {
    ...rest,
    headers,
    credentials: credentials ?? 'include',
  })
  return handleFetchResponse(response)
}

/** Auth headers for raw fetch/SSE (apiClient already injects these automatically). */
export function buildBrowserAuthHeaders(token?: string | null): Record<string, string> {
  const headers: Record<string, string> = {}
  const authToken = token ?? getSessionToken()
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`
  }
  const activeOrgId = getActiveOrganizationId()
  if (activeOrgId) {
    headers['X-Organization-ID'] = String(activeOrgId)
  }
  return headers
}

// Clears token + auth state without "logged out" toast (used when session is already invalid).
let globalSessionInvalidate: (() => void) | null = null

export const setGlobalSessionInvalidate = (fn: () => void) => {
  globalSessionInvalidate = fn
}

const redirectToLoginWithAuthExpired = () => {
  if (isPublicRecordingPath(window.location.pathname)) {
    if (globalSessionInvalidate) {
      globalSessionInvalidate()
    } else {
      clearSessionToken()
    }
    return
  }
  if (globalSessionInvalidate) {
    globalSessionInvalidate()
  } else {
    clearSessionToken()
  }
  const authExpiredMessage = encodeURIComponent('Authentication expired. Please sign in again.')
  const loginUrl = `/login?error=auth_expired&error_description=${authExpiredMessage}`
  // Already on login/join: session is cleared above; full navigation flashes the page.
  if (window.location.pathname === '/login' || window.location.pathname === '/join') {
    return
  }
  window.location.href = loginUrl
}

const isAuthExpiredError = (status: number, errorData: any, requestUrl?: string): boolean => {
  if (status === 401) return true
  if (status !== 403) return false

  const errorText = String(errorData?.error || '').toLowerCase()

  // Keep known business-logic 403 errors as normal in-app errors.
  if (errorText.includes('repository limit reached')) return false
  if (errorText.includes('telemetry is not enabled')) return false

  // Treat explicit auth-expired signals as expired sessions.
  if (errorData?.requires_reauth) return true
  if (errorData?.type === 'auth_expired') return true

  return errorText.includes('authentication') && errorText.includes('expired')
    // Repository save 403 should behave like auth expiration per onboarding UX.
    || Boolean(requestUrl?.includes('/api/repositories'))
}

// Helper function to handle 401 responses for direct fetch calls
export const handleFetchResponse = async (response: Response): Promise<Response> => {
  if (response.status === 401 || response.status === 403) {
    const errorData = await response.clone().json().catch(() => ({ error: 'Unknown error' }))

    // Check if this is a GitHub authentication error that requires re-authentication
    if (errorData.requires_reauth && errorData.re_auth_url) {
      console.warn('GitHub re-authentication required, redirecting to GitHub')
      // Redirect to GitHub for re-authentication
      window.location.href = errorData.re_auth_url
      throw new Error('GitHub re-authentication required')
    }

    if (isAuthExpiredError(response.status, errorData, response.url)) {
      console.warn(`${response.status} auth-expired response received, redirecting to login`)
      redirectToLoginWithAuthExpired()
      throw new Error('Authentication expired - please log in again')
    }
  }
  return response
}

class ApiClient {
  /**
   * Makes an API request with support for self-signed certificates
   * TEMPORARY: This includes workarounds for Let's Encrypt rate limiting
   *
   * Automatically injects Authorization header from localStorage if available.
   * Pass path-only endpoints (e.g. `/api/foo`) — root is injected via {@link apiUrl}.
   */
  async request<T = any>(endpoint: string, options: ApiClientOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      credentials = 'include',
      skipOrganizationHeader = false,
      quiet = false,
    } = options

    const url = apiUrl(endpoint)

    // Automatically inject Authorization header from localStorage if not already provided
    const token = getSessionToken()
    const authHeaders: Record<string, string> = {}
    if (token && !headers['Authorization']) {
      authHeaders['Authorization'] = `Bearer ${token}`
    }
    const activeOrgId = getActiveOrganizationId()
    if (
      activeOrgId &&
      shouldAttachOrganizationHeader(endpoint, Boolean(token), skipOrganizationHeader) &&
      !headers['X-Organization-ID']
    ) {
      authHeaders['X-Organization-ID'] = String(activeOrgId)
    }

    const requestOptions: RequestInit = {
      method,
      credentials,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders,
        ...headers,
      },
    }

    if (body) {
      requestOptions.body = JSON.stringify(body)
    }

    try {
      const response = await fetch(url, requestOptions)

      if (!response.ok) {
        // Handle auth failures (401 + explicit auth-expired 403)
        if (response.status === 401) {
          const errorData = await response.clone().json().catch(() => ({ error: 'Unknown error' }))

          // Check if this is a GitHub authentication error that requires re-authentication
          if (errorData.requires_reauth && errorData.re_auth_url) {
            console.warn('GitHub re-authentication required, redirecting to GitHub')
            // Redirect to GitHub for re-authentication
            window.location.href = errorData.re_auth_url
            throw new Error('GitHub re-authentication required')
          }

          if (isAuthExpiredError(response.status, errorData, url)) {
            console.warn(`${response.status} auth-expired response received, redirecting to login`)
            redirectToLoginWithAuthExpired()
            throw new Error('Authentication expired - please log in again')
          }
        }

        const errorData = await response.json().catch(() => ({} as { error?: string }))
        const fromBody = typeof errorData.error === 'string' ? errorData.error.trim() : ''
        throw new Error(fromBody || `HTTP ${response.status}: ${response.statusText}`)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }

      return await response.text() as T
    } catch (error) {
      if (!quiet) {
        console.error(`API request failed for ${endpoint}:`, error)
      }
      throw error
    }
  }

  // Convenience methods
  async get<T = any>(
    endpoint: string,
    headers?: Record<string, string>,
    options?: Pick<ApiClientOptions, 'skipOrganizationHeader' | 'quiet'>,
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers, ...options })
  }

  async post<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers })
  }

  async put<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers })
  }

  async delete<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers })
  }

  async patch<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

/** True when an API key GET returns 404 (no key generated yet). */
export function isMissingApiKeyError(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).toLowerCase()
  return message.includes('404')
    || message.includes('not found')
    || message.includes('no api key found')
}

// Export the class for testing or custom instances
export { ApiClient }
